import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  authLogin,
  authRegister,
  authRefresh,
  authMe,
  authUpdateProfile,
  authChangePassword,
} from '../lib/api'

// Decode a JWT payload without verification — we don't need to trust it,
// just want the `org` claim to detect impersonation client-side.
// Returns null on malformed tokens.
interface JWTPayload {
  sub?: string
  org?: string
  email?: string
  pa?: boolean
  prole?: string
  ssid?: string
  imode?: 'org' | 'user'
  aid?: string
  aemail?: string
  iuid?: string
  iemail?: string
  sreason?: string
  ticketId?: string
  exp?: number
}
function decodeJwt(token: string | null): JWTPayload | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    // base64url → base64 + add padding (Go's jwt lib emits unpadded base64url
    // and atob requires the input length to be a multiple of 4).
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4
    if (pad) b64 += '='.repeat(4 - pad)
    const json = atob(b64)
    return JSON.parse(json)
  } catch {
    return null
  }
}

export interface User {
  id: string
  email: string
  name: string
  avatar: string
  organizationId: string
  orgName?: string
  roleId: string
  role?: Role
  isActive: boolean
  isPlatformAdmin?: boolean
  platformRole?: string
  platformPermissions?: string[]
  // Per-user instance ACL (PR #27/#30). null/[] = full org access.
  // Populated from `/auth/me` response or decoded from the JWT.
  allowedInstanceIds?: string[]
  // Resolved RBAC verdict from /auth/me. TRUE when the user belongs to at
  // least one team with mask_contact_phones=true. Drives usePhonePrivacy:
  // when true, the per-user toggle is forced ON and disabled in the UI.
  // Undefined / false → per-user opt-in toggle is honoured (legacy).
  effectiveMaskContactPhones?: boolean
  lastLoginAt: string | null
  createdAt: string
}

export interface Organization {
  id: string
  name: string
  slug: string
  plan: string
  maxInstances: number
  maxUsers: number
  settings: Record<string, unknown>
}

export interface Role {
  id: string
  name: string
  slug: string
  permissions: string[]
  scope: string
  canReply: boolean
}

export const useUserStore = defineStore('user', () => {
  const token = ref<string | null>(localStorage.getItem('neogo_token'))
  const refreshTokenValue = ref<string | null>(localStorage.getItem('neogo_refresh_token'))
  const user = ref<User | null>(null)
  const organization = ref<Organization | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  const userName = computed(() => user.value?.name ?? '')
  const userEmail = computed(() => user.value?.email ?? '')
  const userAvatar = computed(() => user.value?.avatar ?? '')
  const userRole = computed(() => user.value?.role ?? null)
  const isPlatformAdmin = computed(() => !!user.value?.isPlatformAdmin)
  const platformRole = computed(() => user.value?.platformRole ?? '')
  const platformPermissions = computed<string[]>(() => user.value?.platformPermissions ?? [])
  const isPlatformOwner = computed(() => platformRole.value === 'platform-owner')
  // Per-user instance ACL — null/[] means "full org access". Components that
  // need to filter UI lists call this and pass to the backend; the backend
  // does the same check (defense in depth, PR #27/#30).
  const allowedInstanceIds = computed<string[] | null>(() => {
    if (user.value?.isPlatformAdmin) return null
    const ids = user.value?.allowedInstanceIds
    if (!ids || ids.length === 0) return null
    return ids
  })
  const orgName = computed(() => organization.value?.name ?? user.value?.orgName ?? '')
  const orgPlan = computed(() => organization.value?.plan ?? '')

  function hasPlatformPermission(permission: string): boolean {
    if (!isPlatformAdmin.value) return false
    if (isPlatformOwner.value) return true
    return platformPermissions.value.includes(permission)
  }

  // Active org id from the JWT — when it differs from user.organizationId
  // (the user's HOME org from the DB), we're impersonating. The banner in
  // AppLayout reads these to render the warning strip + exit button.
  const tokenPayload = computed<JWTPayload | null>(() => decodeJwt(token.value))
  const currentOrgId = computed<string>(() => tokenPayload.value?.org ?? '')
  const supportSession = computed(() => ({
    id: tokenPayload.value?.ssid ?? '',
    mode: tokenPayload.value?.imode ?? '',
    actorUserId: tokenPayload.value?.aid ?? '',
    actorEmail: tokenPayload.value?.aemail ?? '',
    impersonatedUserId: tokenPayload.value?.iuid ?? '',
    impersonatedUserEmail: tokenPayload.value?.iemail ?? '',
    reason: tokenPayload.value?.sreason ?? '',
    ticketId: tokenPayload.value?.ticketId ?? '',
  }))
  const isImpersonating = computed<boolean>(() => {
    const home = user.value?.organizationId
    const active = currentOrgId.value
    return !!tokenPayload.value?.ssid || (!!home && !!active && home !== active)
  })

  function setTokens(access: string, refresh: string) {
    token.value = access
    refreshTokenValue.value = refresh
    localStorage.setItem('neogo_token', access)
    localStorage.setItem('neogo_refresh_token', refresh)
  }

  function clearTokens() {
    token.value = null
    refreshTokenValue.value = null
    user.value = null
    organization.value = null
    localStorage.removeItem('neogo_token')
    localStorage.removeItem('neogo_refresh_token')
    localStorage.removeItem('neogo_apikey')
  }

  async function login(email: string, password: string): Promise<boolean> {
    try {
      const res = await authLogin({ email, password })
      const data = res.data
      setTokens(data.token, data.refreshToken)
      user.value = data.user
      organization.value = data.organization
      return true
    } catch {
      return false
    }
  }

  async function register(companyName: string, name: string, email: string, password: string): Promise<boolean> {
    try {
      const res = await authRegister({ companyName, name, email, password })
      const data = res.data
      setTokens(data.token, data.refreshToken)
      user.value = data.user
      organization.value = data.organization
      return true
    } catch {
      return false
    }
  }

  function logout() {
    clearTokens()
    // Router push handled by caller
  }

  async function doRefreshToken(): Promise<boolean> {
    if (!refreshTokenValue.value) return false
    try {
      const res = await authRefresh({ refreshToken: refreshTokenValue.value })
      const data = res.data
      setTokens(data.token, data.refreshToken)
      return true
    } catch {
      clearTokens()
      return false
    }
  }

  async function fetchMe(): Promise<boolean> {
    try {
      const res = await authMe()
      // GET /auth/me returns the UserResponse DTO directly (no { user, organization }
      // wrapper — that shape is only used by /auth/login + /auth/register).
      // Treat the body as the user; organization stays whatever was set at login.
      const data = res.data
      if (data && typeof data === 'object' && 'id' in data) {
        user.value = data as User
      } else if (data?.user) {
        // Defensive: tolerate a wrapped response in case the backend ever changes.
        user.value = data.user
        if (data.organization) organization.value = data.organization
      }
      return true
    } catch {
      return false
    }
  }

  async function updateProfile(data: { name?: string; avatar?: string }): Promise<boolean> {
    try {
      await authUpdateProfile(data)
      if (user.value) {
        if (data.name) user.value.name = data.name
        if (data.avatar) user.value.avatar = data.avatar
      }
      return true
    } catch {
      return false
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    try {
      await authChangePassword({ currentPassword, newPassword })
      return true
    } catch {
      return false
    }
  }

  return {
    token,
    refreshTokenRef: refreshTokenValue,
    user,
    organization,
    isAuthenticated,
    userName,
    userEmail,
    userAvatar,
    userRole,
    isPlatformAdmin,
    platformRole,
    platformPermissions,
    isPlatformOwner,
    hasPlatformPermission,
    allowedInstanceIds,
    orgName,
    orgPlan,
    currentOrgId,
    supportSession,
    isImpersonating,
    login,
    register,
    logout,
    refreshToken: doRefreshToken,
    fetchMe,
    updateProfile,
    changePassword,
    setTokens,
    clearTokens,
  }
})
